import classNames from 'classnames'
import { Override } from 'maeng-type/Common'
import { uuid } from 'maeng-utils/utils'
import {
  ComponentPropsWithoutRef,
  ElementType,
  ForwardedRef,
  Fragment,
  JSXElementConstructor,
  ReactElement,
  ReactNode,
  forwardRef,
  useCallback,
  useMemo,
  MouseEvent,
  useRef,
  useImperativeHandle,
} from 'react'

export type MenuType<T extends ElementType> = Override<
  ComponentPropsWithoutRef<T>,
  {
    key?: string | number
    label: ReactNode
    active?: boolean
    disabled?: boolean
    children?: MenuType<T>[]
    component?: T
  }
>

export type MenuWithParent<T extends ElementType> = Omit<MenuType<T>, 'key' | 'children'> & {
  key: string | number
  parentKey?: string | number
}

export type MenuProps<T extends ElementType> = {
  menus: MenuType<T>[]
  collapsed?: boolean
  depth?: boolean
  size?: 'xs' | 'sm' | 'md' | 'lg'
  className?: string
  direction?: 'vertical' | 'horizontal'
}

export const Menu = forwardRef(
  <T extends ElementType>(
    {
      menus,
      collapsed = false,
      depth = false,
      size = 'md',
      className: classProps = '',
      direction = 'vertical',
      ...props
    }: MenuProps<T>,
    ref: ForwardedRef<HTMLDivElement>,
  ): ReactElement<any, string | JSXElementConstructor<any>> | null => {
    const innerRef = useRef<HTMLDivElement>(null)
    useImperativeHandle(ref, () => innerRef?.current!)

    const className = useMemo(() => {
      const sizes = {
        'menu-xs': size === 'xs',
        'menu-sm': size === 'sm',
        'menu-md': size === 'md',
        'menu-lg': size === 'lg',
      }

      const directions = {
        'menu-vertical': direction === 'vertical',
        'menu-horizontal': direction === 'horizontal',
      }
      return classNames({
        menu: true,
        ...sizes,
        ...directions,
        [classProps]: true,
      })
    }, [classProps, direction, size])

    const hasActiveChild = useCallback((menuList: MenuType<T>[]) => {
      // eslint-disable-next-line no-restricted-syntax
      for (const menu of menuList) {
        if (menu.active === true) {
          return true
        }
        if (menu.children && hasActiveChild(menu.children)) {
          return true
        }
      }
      return false
    }, [])

    const renderItem = useCallback(
      (item: MenuType<T>) => {
        const { active, label, component: ItemComponent, ...restProps } = item
        if (ItemComponent) {
          return (
            <ItemComponent
              className={classNames({
                active: !!active,
                'pointer-events-none': item.disabled,
                'whitespace-nowrap': direction === 'horizontal',
              })}
              {...(restProps as any)}
            >
              {label}
            </ItemComponent>
          )
        }
        if (item.href) {
          return (
            <a
              className={classNames({
                active: !!active,
                'pointer-events-none': item.disabled,
                'whitespace-nowrap': direction === 'horizontal',
              })}
              {...restProps}
            >
              {label}
            </a>
          )
        }
        return (
          <div
            className={classNames({
              active: !!active,
              'pointer-events-none': item.disabled,
              'whitespace-nowrap': direction === 'horizontal',
            })}
            {...restProps}
          >
            {label}
          </div>
        )
      },
      [direction],
    )

    const renderCollapsedDepthMenu = useCallback(
      (menu: MenuType<T>[]): ReactNode => {
        return menu.map((item) => {
          return (
            <li className={classNames({ disabled: item.disabled })} key={item.key ?? uuid()}>
              {item.children && (
                <details open={item.active || hasActiveChild(item.children)}>
                  <summary>
                    <div className="menu-title !p-0">{item.label}</div>
                  </summary>
                  <ul>
                    {renderCollapsedDepthMenu(
                      item.children.map((child) => ({
                        ...child,
                        disabled: item.disabled || child.disabled,
                      })),
                    )}
                  </ul>
                </details>
              )}
              {!item.children && renderItem(item)}
            </li>
          )
        })
      },
      [hasActiveChild, renderItem],
    )

    const renderNoCollapsedDepthMenu = useCallback(
      (menu: MenuType<T>[]): ReactNode => {
        return menu.map((item) => {
          return (
            <li className={classNames({ disabled: item.disabled })} key={item.key ?? uuid()}>
              {item.children && (
                <>
                  <h2 className="menu-title">{renderItem(item)}</h2>
                  <ul>
                    {renderNoCollapsedDepthMenu(
                      item.children.map((child) => ({
                        ...child,
                        disabled: item.disabled || child.disabled,
                      })),
                    )}
                  </ul>
                </>
              )}
              {!item.children && renderItem(item)}
            </li>
          )
        })
      },
      [renderItem],
    )

    const flattenMenu = useCallback((menuList: MenuType<T>[], parentKey?: string | number) => {
      return menuList.reduce<MenuWithParent<T>[]>(
        (flattenedMenu, { key, disabled, children, ...restProps }) => {
          const menuEntry = {
            key: key || uuid(),
            disabled,
            parentKey: children ? undefined : parentKey,
            ...restProps,
          } as MenuWithParent<T>
          flattenedMenu.push(
            menuEntry,
            ...(children
              ? flattenMenu(
                  children.map((child) => ({ ...child, disabled: disabled || child.disabled })),
                  key,
                )
              : []),
          )
          return flattenedMenu
        },
        [],
      )
    }, [])

    const organizeMenuWithChildren = useCallback((menuList: MenuWithParent<T>[]) => {
      const menuMap = new Map<string | number, MenuType<T>>()
      menuList.forEach(({ key, label, parentKey, ...restProps }) => {
        const menuItem = {
          key,
          label,
          ...restProps,
        } as unknown as MenuType<T>

        if (menuMap.has(key)) {
          menuItem.children = menuMap.get(key)!.children
        }

        if (parentKey && menuMap.has(parentKey)) {
          const parentItem = menuMap.get(parentKey)!
          parentItem.children = parentItem.children || []
          parentItem.children.push(menuItem)
        } else {
          menuMap.set(key, menuItem)
        }
      })

      return Array.from(menuMap.values())
    }, [])

    const renderVerticalCollapsedNoDepthMenu = useCallback(
      (menu: MenuType<T>[]) => {
        // Collapsed and No Depth
        return organizeMenuWithChildren(flattenMenu(menu)).map((item, idx) => {
          if (!item.children) {
            return (
              <Fragment key={item.key}>
                {idx !== 0 && <div className="divider m-0" />}
                <ul className={`${className} p-0`}>
                  <li className={classNames({ disabled: item.disabled })}>
                    <a className={classNames({ active: item.active })} href={item.href}>
                      {item.label}
                    </a>
                  </li>
                </ul>
              </Fragment>
            )
          }
          return (
            <Fragment key={item.key}>
              {idx !== 0 && <div className="divider m-0" />}
              <ul className={`${className} p-0`}>
                <li className={item.disabled ? 'disabled' : ''}>
                  <details open={item.active || hasActiveChild(item.children)}>
                    <summary>
                      <div className="menu-title !p-0">{item.label}</div>
                    </summary>
                    <ul className="m-0 !p-0 before:content-none">
                      {item.children.map((child) => (
                        <li
                          className={classNames({ disabled: item.disabled || child.disabled })}
                          key={child.key}
                        >
                          {renderItem({ ...child, disabled: item.disabled || child.disabled })}
                        </li>
                      ))}
                    </ul>
                  </details>
                </li>
              </ul>
            </Fragment>
          )
        })
      },
      [className, flattenMenu, hasActiveChild, organizeMenuWithChildren, renderItem],
    )

    const renderVerticalNoCollapsedNoDepthMenu = useCallback(
      (menu: MenuType<T>[]) => {
        // No Collapsed and No Depth
        return organizeMenuWithChildren(flattenMenu(menu)).map((item, idx) => {
          if (!item.children) {
            return (
              <Fragment key={item.key}>
                {idx !== 0 && <div className="divider m-0" />}
                <ul className={`${className} p-0`}>
                  <li className={classNames({ disabled: item.disabled })}>{renderItem(item)}</li>
                </ul>
              </Fragment>
            )
          }
          return (
            <Fragment key={item.key}>
              {idx !== 0 && <div className="divider m-0" />}
              <ul className={`${className} p-0`}>
                <li className={item.disabled ? 'disabled' : ''}>
                  <h2 className="menu-title">{renderItem(item)}</h2>
                </li>
                {item.children.map((child) => (
                  <li
                    className={classNames({
                      disabled: item.disabled || child.disabled,
                    })}
                    key={child.key}
                  >
                    {renderItem({ ...child, disabled: item.disabled || child.disabled })}
                  </li>
                ))}
              </ul>
            </Fragment>
          )
        })
      },
      [className, flattenMenu, organizeMenuWithChildren, renderItem],
    )

    const renderHorizontalCollapsedNoDepthMenu = useCallback(
      (menu: MenuType<T>[]) => {
        // Collapsed and No Depth
        return organizeMenuWithChildren(flattenMenu(menu)).map((menuItem) => {
          const MENU_GROUP_CLASS = 'menu_group'
          const item = {
            ...menuItem,
            onClick: (e: MouseEvent) => {
              menuItem.onClick?.(e)

              if ((e.target as Element)?.closest('.menu-title')) return

              const isMountedMenu = typeof innerRef !== 'function' && !!innerRef?.current
              if (!isMountedMenu) return

              innerRef.current?.querySelectorAll(`.${MENU_GROUP_CLASS}`).forEach((el) => {
                el.removeAttribute('open')
              })
            },
          }
          return (
            <li className={classNames({ disabled: item.disabled })} key={item.key ?? uuid()}>
              {item.children && (
                <details className={MENU_GROUP_CLASS}>
                  <summary className="py-0">
                    <h2 className="menu-title !text-inherit !px-0">{renderItem(item)}</h2>
                  </summary>
                  <ul>
                    {renderHorizontalCollapsedNoDepthMenu(
                      item.children.map((child) => ({
                        ...child,
                        disabled: item.disabled || child.disabled,
                      })),
                    )}
                  </ul>
                </details>
              )}
              {!item.children && renderItem(item)}
            </li>
          )
        })
      },
      [flattenMenu, organizeMenuWithChildren, renderItem],
    )

    const renderHorizontalNoCollapsedNoDepthMenu = useCallback(
      (menu: MenuType<T>[]) => {
        // Collapsed and No Depth
        return organizeMenuWithChildren(flattenMenu(menu)).map((item) => {
          return (
            <li className={classNames({ disabled: item.disabled })} key={item.key ?? uuid()}>
              {item.children && (
                <>
                  <h2 className="menu-title">{renderItem(item)}</h2>
                  <ul className="m-0 p-0 before:content-none">
                    {renderNoCollapsedDepthMenu(
                      item.children.map((child) => ({
                        ...child,
                        disabled: item.disabled || child.disabled,
                      })),
                    )}
                  </ul>
                </>
              )}
              {!item.children && renderItem(item)}
            </li>
          )
        })
      },
      [flattenMenu, organizeMenuWithChildren, renderItem, renderNoCollapsedDepthMenu],
    )

    // Collapsed and Depth
    if (collapsed && depth)
      return (
        <div ref={innerRef}>
          <ul className={className} {...props}>
            {renderCollapsedDepthMenu(menus)}
          </ul>
        </div>
      )

    // No Collapsed and Depth
    if (!collapsed && depth)
      return (
        <div ref={innerRef}>
          <ul className={className} {...props}>
            {renderNoCollapsedDepthMenu(menus)}
          </ul>
        </div>
      )

    // Vertical, Collapsed and No Depth
    if (direction === 'vertical' && collapsed && !depth)
      return <div ref={innerRef}>{renderVerticalCollapsedNoDepthMenu(menus)}</div>

    // Horizontal, Collapsed and No Depth
    if (direction === 'horizontal' && collapsed && !depth)
      return (
        <div ref={innerRef}>
          <ul className={className} {...props}>
            {renderHorizontalCollapsedNoDepthMenu(menus)}
          </ul>
        </div>
      )

    // Vertical, No Collapsed and No Depth
    if (direction === 'vertical' && !collapsed && !depth)
      return <div ref={innerRef}>{renderVerticalNoCollapsedNoDepthMenu(menus)}</div>

    // Horizontal, No Collapsed and No Depth
    if (direction === 'horizontal' && !collapsed && !depth)
      return (
        <div ref={innerRef}>
          <ul className={className} {...props}>
            {renderHorizontalNoCollapsedNoDepthMenu(menus)}
          </ul>
        </div>
      )

    return null
  },
)

export default Menu
